import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'he';
interface Translations {
  nav: {
    issues: string;
    about: string;
    submit: string;
    contact: string;
  };
  issues: {
    title: string;
    viewPdf: string;
  };
  about: {
    title: string;
    content: string;
  };
  submit: {
    title: string;
    intro: string;
    guidelines: string;
    guidelinesList: string[];
    emailLabel: string;
    emailPlaceholder: string;
    submitButton: string;
  };
  contact: {
    title: string;
    intro: string;
    emailLabel: string;
    email: string;
    addressLabel: string;
    address: string;
    formTitle: string;
    nameLabel: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    sendButton: string;
  };
  footer: {
    copyright: string;
  };
}
const translations: Record<Language, Translations> = {
  en: {
    nav: {
      issues: 'Issues',
      about: 'About',
      submit: 'Submit',
      contact: 'Contact'
    },
    issues: {
      title: 'Past Issues',
      viewPdf: 'View PDF'
    },
    about: {
      title: 'About Us',
      content: 'We are a student-run  newspaper dedicated to exploring the arts, literature, and cultural discourse on campus. Founded by students passionate about storytelling. Each issue features essays, reviews, interviews, and creative writing that reflect the vibrant cultural life of our community.'
    },
    submit: {
      title: 'Submit Your Work',
      intro: 'We welcome submissions from all students interested in contributing to our cultural dialogue.',
      guidelines: 'Submission Guidelines:',
      guidelinesList: ['Essays and articles: 800-2000 words', 'Reviews: 500-1000 words', 'Creative writing: poetry, short fiction, creative nonfiction', 'Visual art and photography welcome', 'All submissions should be original work'],
      emailLabel: 'Send submissions to:',
      emailPlaceholder: 'submissions.stjournal@gmail.com',
      submitButton: 'Email Your Submission'
    },
    contact: {
      title: 'Contact Us',
      intro: 'Get in touch with our editorial team.',
      emailLabel: 'Email:',
      email: 'reference.studentjournal@gmail.com',
      addressLabel: 'Address:',
      address: 'Student Media Center, Campus Building 12',
      formTitle: 'Send us a message',
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'your.email@example.com',
      messageLabel: 'Message',
      messagePlaceholder: 'Your message...',
      sendButton: 'Send Message'
    },
    footer: {
      copyright: '© 2026 Reference Journal. All rights reserved.'
    }
  },
  he: {
    nav: {
      issues: 'גיליונות',
      about: 'אודות',
      submit: 'שלחו',
      contact: 'צרו קשר'
    },
    issues: {
      title: 'גיליונות קודמים',
      viewPdf: 'צפה ב-PDF'
    },
    about: {
      title: 'אודותינו',
      content: 'אנחנו עיתון תרבות סטודנטיאלי המוקדש לחקר האמנויות, הספרות והשיח התרבותי בקמפוס. נוסד על ידי סטודנטים הנלהבים מסיפור סיפורים, הפרסום שלנו משמש כפלטפורמה לקולות ונקודות מבט מגוונות. כל גיליון כולל מאמרים, ביקורות, ראיונות וכתיבה יצירתית המשקפים את חיי התרבות התוססים של הקהילה שלנו.'
    },
    submit: {
      title: 'שלח את עבודתך',
      intro: 'אנו מקבלים בברכה הגשות מכל הסטודנטים המעוניינים לתרום לדיאלוג התרבותי שלנו.',
      guidelines: 'הנחיות הגשה:',
      guidelinesList: ['מאמרים ומסות: 800-2000 מילים', 'ביקורות: 500-1000 מילים', 'כתיבה יצירתית: שירה, סיפורת קצרה, עיון יצירתי', 'אמנות חזותית וצילום מתקבלים בברכה', 'כל ההגשות צריכות להיות עבודה מקורית'],
      emailLabel: 'שלח הגשות אל:',
      emailPlaceholder: 'submissions.stjournal@gmail.com',
      submitButton: 'שלח את ההגשה שלך'
    },
    contact: {
      title: 'צור קשר',
      intro: 'צור קשר עם צוות העריכה שלנו.',
      emailLabel: 'דוא"ל:',
      email: 'reference.studentjournal@gmail.com',
      addressLabel: 'כתובת:',
      address: 'מרכז מדיה סטודנטיאלי, בניין קמפוס 12',
      formTitle: 'שלח לנו הודעה',
      nameLabel: 'שם',
      namePlaceholder: 'השם שלך',
      emailPlaceholder: 'your.email@example.com',
      messageLabel: 'הודעה',
      messagePlaceholder: 'ההודעה שלך...',
      sendButton: 'שלח הודעה'
    },
    footer: {
      copyright: '© 2026 חדשות תרבות. כל הזכויות שמורות.'
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRTL: boolean;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  useEffect(() => {
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);
  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
    isRTL: language === 'he'
  };
  return <LanguageContext.Provider value={value} data-id="element-70">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}